import {
  DbotEvent,
  DecisionContext,
  DbotEventDispatcher,
  extractSectionWithMaxScoreFromArticles,
} from "../common/commonDomain";
import { makeAutoObservable } from "mobx";
import { SuggestedCompany } from "../../types/CompanyInfo";
import { DecisionType } from "../../common/decisions";
import { ArticleSelectorModel, StringFieldModel } from "../../common/commonForms";

export type CompanyNameChange = {
  type: DecisionType.companyName;
  newCompanyName: string;
  articleOfAssociationNumber?: number;
};

export class CompanyNameChangeForm {
  readonly type = DecisionType.companyName;
  readonly dispatch: DbotEventDispatcher;
  readonly context: DecisionContext;
  newCompanyName: StringFieldModel;
  articleOfAssociationNumber: ArticleSelectorModel;
  suggestedCompanies: SuggestedCompany[] = [];

  constructor(
    dispatch: DbotEventDispatcher,
    context: DecisionContext,
    public data?: CompanyNameChange,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.dispatch = dispatch;
    this.context = context;
    this.newCompanyName = new StringFieldModel({
      value: data?.newCompanyName,
      required: true,
      onChange: (value) => value !== undefined && this.onCompanyNameChange(value),
      validator: (value) =>
        includesCompanyName(this.suggestedCompanies, value)
          ? { key: "decisions.companyName.match.brreg" }
          : undefined,
    });

    this.articleOfAssociationNumber = new ArticleSelectorModel(
      context.inputState?.articles,
      data?.articleOfAssociationNumber,
    );
  }

  init(): void {
    if (this.data === undefined || this.data.articleOfAssociationNumber === undefined) {
      this.dispatch({
        type: "dbot/explore-articles-of-association",
        articles: this.context.inputState?.articles,
      });
    }
  }

  onCompanyNameChange(value: string) {
    const input = normalizeCompanyName(value);
    this.dispatch({ type: "dbot/search-companies", input, source: this });
  }

  setCompanyNames(suggestedCompanies: SuggestedCompany[]) {
    this.suggestedCompanies = suggestedCompanies;
    this.newCompanyName.validate();
  }

  handleEvent(event: DbotEvent): void {
    switch (event.type) {
      case "dbot/explore-articles-of-association-success":
        this.articleOfAssociationNumber?.setSelected(
          extractSectionWithMaxScoreFromArticles(event.data.results, "COMPANYNAME"),
        );
        break;
      case "dbot/search-companies-result":
        if (event.source === this) {
          this.setCompanyNames(event.result);
        }
        break;
    }
  }

  get articlesOfAssociation() {
    return this.context.inputState?.articles;
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return this.newCompanyName.isValid && this.articleOfAssociationNumber.isValid;
  }

  validate(): boolean {
    this.newCompanyName.validate();
    return this.isValid;
  }
}

export function handleEvent(form: CompanyNameChangeForm, event: DbotEvent) {
  form.handleEvent(event);
}

export function normalizeCompanyName(value: string) {
  return value.replaceAll(/\s+/g, " ").trim().toLowerCase();
}

export function includesCompanyName(companies: SuggestedCompany[], value: string) {
  const inputValue = normalizeCompanyName(value);
  const suggestedCompanyNames = companies.map((s) => normalizeCompanyName(s.name));
  return suggestedCompanyNames.includes(inputValue);
}
